/**
 * ApplyHeader — single, consolidated header for the /apply flow.
 *
 * Replaces the previous three-strip stack (BorrowerHeader + WelcomeCheck +
 * SaveProgressBar) with one bar:
 *
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ [LK]   ●─Assessment──○─Application──○─Documents──○─Verdict   [👤▾] │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * Behaviors:
 *   - The logo is intentionally NOT a navigation link inside /apply (the
 *     previous "logo takes me home" caused users to lose work). Use the menu.
 *   - The stepper shows macro stages with completed checkmarks and lets users
 *     jump back to a completed stage with one click.
 *   - The user menu is the single place for: auto-save status, dashboard,
 *     save now, exit, and sign out.
 *
 * Usage:
 *   <ApplyHeader
 *     currentStep={currentStep}
 *     onStepClick={(stepKey) => dispatch(setCurrentStep(STEPS[stepKey]))}
 *     onSaveNow={handleSaveProgress}
 *     onSignOut={handleLogout}
 *     onSignIn={handleLoginRedirect}
 *     lastSaved={lastSaved}
 *   />
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Check, ChevronDown, LayoutDashboard, LogIn, LogOut,
  RotateCcw, Save, DoorOpen, User as UserIcon,
} from 'lucide-react';
import { STEPS } from '../../store/loanApplicationSlice';

// Macro stages displayed in the top stepper. These are the only stages the
// borrower really needs to feel oriented in. Sub-step granularity ("question
// 3 of 7") lives inside each page.
const STAGES = [
  { key: 'ASSESSMENT',   label: 'Assessment',   step: STEPS.ASSESSMENT },
  { key: 'APPLICATION',  label: 'Application',  step: STEPS.APPLICATION },
  { key: 'DOCUMENTS',    label: 'Documents',    step: STEPS.DOCUMENTS },
  { key: 'UNDERWRITING', label: 'Owen\'s Verdict', step: STEPS.UNDERWRITING },
];

const stageIndexFor = (currentStep) => {
  // Group all underwriting/lender steps under the "Verdict" stage.
  if (currentStep >= STEPS.UNDERWRITING_PICKER) return 3;
  if (currentStep === STEPS.DOCUMENTS) return 2;
  if (currentStep === STEPS.APPLICATION) return 1;
  return 0;
};

const formatRelative = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ApplyHeader = ({
  currentStep,
  onStepClick,
  onSaveNow,
  onSignOut,
  onSignIn,
  onStartOver,
  lastSaved,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((s) => s.loanApplication);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const activeIdx = stageIndexFor(currentStep);
  const savedLabel = formatRelative(lastSaved);

  const initials = (user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <header style={{
      borderBottom: '1px solid var(--lk-border)',
      backgroundColor: 'var(--lk-bg-surface-1)',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: '10px var(--lk-space-4)',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center', gap: 24,
      }}>
        {/* Brand mark — intentionally non-navigating inside /apply */}
        <div
          aria-label="LoanKnow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <img
            src="/assets/loanknow-logo.svg"
            alt="LoanKnow"
            style={{ height: 36, display: 'block' }}
          />
        </div>

        {/* Macro Stepper */}
        <nav
          aria-label="Application progress"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, overflowX: 'auto',
          }}
          className="lk-apply-stepper"
        >
          {STAGES.map((stage, idx) => {
            const isActive = idx === activeIdx;
            const isComplete = idx < activeIdx;
            const isClickable = isComplete && typeof onStepClick === 'function';
            return (
              <div key={stage.key} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(stage.key)}
                  disabled={!isClickable}
                  title={isComplete ? `Go back to ${stage.label}` : isActive ? `Current step: ${stage.label}` : `Locked: ${stage.label}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', borderRadius: 999,
                    border: 'none',
                    backgroundColor: isActive
                      ? 'var(--lk-primary-50)'
                      : isComplete ? 'transparent' : 'transparent',
                    color: isActive
                      ? 'var(--lk-primary-600)'
                      : isComplete ? 'var(--lk-text-secondary)' : 'var(--lk-text-quaternary)',
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    cursor: isClickable ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: isComplete
                        ? 'var(--lk-success-500)'
                        : isActive ? 'var(--lk-primary-500)' : 'transparent',
                      color: isComplete || isActive ? 'white' : 'var(--lk-text-quaternary)',
                      border: !isComplete && !isActive ? '1.5px solid var(--lk-border)' : 'none',
                      fontSize: 11, fontWeight: 700,
                    }}
                  >
                    {isComplete ? <Check size={13} /> : idx + 1}
                  </span>
                  <span>{stage.label}</span>
                </button>
                {idx < STAGES.length - 1 && (
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block', width: 18, height: 1,
                      backgroundColor: idx < activeIdx ? 'var(--lk-success-300)' : 'var(--lk-border)',
                      margin: '0 2px',
                    }}
                  />
                )}
              </div>
            );
          })}
        </nav>

        {/* Dev reset + user menu / Sign in */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
          {typeof onStartOver === 'function' && (
            <button
              type="button"
              className="lk-apply-start-over"
              onClick={onStartOver}
              title="Clear answers and restart the application"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                borderRadius: 999,
                border: '2px solid #d97706',
                backgroundColor: '#fffbeb',
                color: '#92400e',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <RotateCcw size={14} />
              <span className="lk-start-over-label">Start over</span>
            </button>
          )}
        <div style={{ position: 'relative' }} ref={menuRef}>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 10px 6px 6px', borderRadius: 999,
                border: '1px solid var(--lk-border)',
                backgroundColor: menuOpen ? 'var(--lk-bg-surface-2)' : 'white',
                cursor: 'pointer',
              }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--lk-primary-500), var(--lk-purple-600, #7c3aed))',
                color: 'white', fontSize: 12, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {initials}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--lk-text-primary)' }}>
                {user?.firstName || user?.email?.split('@')[0] || 'Account'}
              </span>
              <ChevronDown size={14} color="var(--lk-text-tertiary)" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSignIn}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 999,
                border: '1px solid var(--lk-border)',
                backgroundColor: 'white', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: 'var(--lk-text-primary)',
              }}
            >
              <LogIn size={14} /> Sign in to save
            </button>
          )}

          {menuOpen && isAuthenticated && (
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                width: 260, padding: 8,
                backgroundColor: 'white',
                borderRadius: 12, border: '1px solid var(--lk-border)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.10)',
                zIndex: 60,
              }}
            >
              <div style={{ padding: '10px 12px 8px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--lk-text-primary)' }}>
                  {user?.firstName ? `${user.firstName} ${user?.lastName || ''}`.trim() : user?.email}
                </div>
                {user?.email && user?.firstName && (
                  <div style={{ fontSize: 12, color: 'var(--lk-text-tertiary)' }}>{user.email}</div>
                )}
                <div style={{
                  marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 11, fontWeight: 600,
                  color: savedLabel ? 'var(--lk-success-700)' : 'var(--lk-text-tertiary)',
                  backgroundColor: savedLabel ? 'var(--lk-success-50)' : 'var(--lk-bg-surface-2)',
                  padding: '3px 8px', borderRadius: 999,
                }}>
                  <Check size={11} />
                  {savedLabel ? `Auto-saved ${savedLabel}` : 'Auto-save on'}
                </div>
              </div>
              <div style={{ height: 1, backgroundColor: 'var(--lk-border-subtle)', margin: '4px 0' }} />
              <MenuItem
                icon={<LayoutDashboard size={15} />}
                label="My Dashboard"
                onClick={() => { setMenuOpen(false); navigate('/borrower/dashboard'); }}
              />
              <MenuItem
                icon={<Save size={15} />}
                label="Save now"
                onClick={() => { setMenuOpen(false); onSaveNow?.(); }}
              />
              <MenuItem
                icon={<DoorOpen size={15} />}
                label="Exit application"
                onClick={() => { setMenuOpen(false); onSaveNow?.(); navigate('/'); }}
              />
              <div style={{ height: 1, backgroundColor: 'var(--lk-border-subtle)', margin: '4px 0' }} />
              <MenuItem
                icon={<LogOut size={15} />}
                label="Sign out"
                onClick={() => { setMenuOpen(false); onSignOut?.(); }}
                danger
              />
            </div>
          )}

          {menuOpen && !isAuthenticated && (
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                width: 240, padding: 8,
                backgroundColor: 'white',
                borderRadius: 12, border: '1px solid var(--lk-border)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.10)',
                zIndex: 60,
              }}
            >
              <MenuItem
                icon={<UserIcon size={15} />}
                label="Sign in / Create account"
                onClick={() => { setMenuOpen(false); onSignIn?.(); }}
              />
              <MenuItem
                icon={<DoorOpen size={15} />}
                label="Exit to home"
                onClick={() => { setMenuOpen(false); navigate('/'); }}
              />
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Mobile-friendly stepper scroll */}
      <style>{`
        @media (max-width: 720px) {
          .lk-apply-stepper button > span:last-child { display: none; }
          .lk-apply-start-over span.lk-start-over-label { display: none; }
        }
      `}</style>
    </header>
  );
};

const MenuItem = ({ icon, label, onClick, danger = false }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 12px', borderRadius: 8,
      border: 'none', backgroundColor: 'transparent',
      cursor: 'pointer', textAlign: 'left',
      fontSize: 13, fontWeight: 500,
      color: danger ? 'var(--lk-danger-600)' : 'var(--lk-text-primary)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--lk-bg-surface-2)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <span style={{ color: danger ? 'var(--lk-danger-500)' : 'var(--lk-text-tertiary)', display: 'inline-flex' }}>
      {icon}
    </span>
    {label}
  </button>
);

export default ApplyHeader;
